class JpCartDrawer extends HTMLElement {
  connectedCallback() {
    this.cartUrl = this.getAttribute('data-cart-url') || '/cart';
    this.recommendationsUrl = this.getAttribute('data-recommendations-url') || '/recommendations/products';

    this.panel = this.querySelector('.jp-cart-drawer__panel');
    this.itemsEl = this.querySelector('[data-jp-cart-items]');
    this.emptyEl = this.querySelector('[data-jp-cart-empty]');
    this.countEl = this.querySelector('[data-jp-cart-count]');
    this.totalEl = this.querySelector('[data-jp-cart-total]');
    this.recommendationsEl = this.querySelector('[data-jp-cart-recommendations]');
    this.recommendationsListEl = this.querySelector('[data-jp-cart-recommendations-list]');
    this.giftEl = this.querySelector('[data-jp-cart-gift]');

    this.subtitles = {};
    const subtitlesScript = document.querySelector('[data-jp-cart-subtitles]');
    if (subtitlesScript) {
      try {
        this.subtitles = JSON.parse(subtitlesScript.textContent);
      } catch {
        this.subtitles = {};
      }
    }

    this.querySelectorAll('[data-jp-cart-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.code === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.fetchAndRenderCart();
        this.open();
      });
    }

    this.fetchAndRenderCart();
  }

  getSectionsToRender() {
    return [];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents() {
    this.fetchAndRenderCart();
    this.open();
  }

  open() {
    this.classList.add('is-open');
    document.documentElement.classList.add('jp-cart-drawer-open');
    this.setAttribute('aria-hidden', 'false');
    this.panel.focus();
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('jp-cart-drawer-open');
    this.setAttribute('aria-hidden', 'true');
  }

  fetchAndRenderCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => this.renderCart(cart))
      .catch(() => {});
  }

  renderCart(cart) {
    this.cart = cart;

    if (this.countEl) {
      this.countEl.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'Item' : 'Items'})`;
    }
    if (this.totalEl) {
      this.totalEl.textContent = this.formatMoney(cart.total_price);
    }

    if (cart.item_count === 0) {
      this.itemsEl.innerHTML = '';
      this.emptyEl.hidden = false;
    } else {
      this.emptyEl.hidden = true;

      // Bundle-tier upsell note ("Add N more to unlock X% off") — shares the
      // same config/logic as the full cart page (assets/jp-bundle-upsell.js).
      let bundleConfig = null;
      let bundleNote = '';
      if (window.JPBundleUpsell) {
        bundleConfig = window.JPBundleUpsell.readConfig();
        if (bundleConfig) {
          const totalQty = window.JPBundleUpsell.sumBundleQtyFromCartItems(bundleConfig, cart.items);
          bundleNote = window.JPBundleUpsell.computeNote(bundleConfig, totalQty);
        }
      }

      this.itemsEl.innerHTML = cart.items
        .map((item) => this.renderItem(item, bundleConfig, bundleNote))
        .join('');
      this.itemsEl.querySelectorAll('[data-jp-qty-decrease]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeLineQuantity(btn, -1));
      });
      this.itemsEl.querySelectorAll('[data-jp-qty-increase]').forEach((btn) => {
        btn.addEventListener('click', () => this.changeLineQuantity(btn, 1));
      });
      this.itemsEl.querySelectorAll('[data-jp-item-remove]').forEach((btn) => {
        btn.addEventListener('click', () => this.removeLine(btn));
      });
    }

    this.updateCartIconBubble(cart.item_count);
    this.loadRecommendations();

    // Placeholder only — just messaging to build excitement, no real
    // product or line item behind it.
    if (this.giftEl) this.giftEl.hidden = cart.item_count === 0;
  }

  renderItem(item, bundleConfig, bundleNote) {
    const image = item.image
      ? `<img class="jp-cart-drawer__item-image" src="${item.image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_240x$1$2')}" alt="${this.escapeHtml(item.title)}" loading="lazy">`
      : '';
    const metafieldSubtitle = this.subtitles[String(item.product_id)];
    const subtitle = metafieldSubtitle
      ? this.escapeHtml(metafieldSubtitle)
      : item.variant_title
      ? this.escapeHtml(item.variant_title)
      : item.product_type
      ? this.escapeHtml(item.product_type)
      : '';

    const showBundleNote = bundleConfig && bundleConfig.productIds.includes(item.product_id) && bundleNote;

    return `
      <div class="jp-cart-drawer__item" data-jp-line-key="${item.key}">
        <div class="jp-cart-drawer__item-image-wrap">${image}</div>
        <div class="jp-cart-drawer__item-info">
          <div class="jp-cart-drawer__item-name-block">
            <p class="jp-cart-drawer__item-name">${this.escapeHtml(item.product_title)}</p>
            ${subtitle ? `<p class="jp-cart-drawer__item-subtitle">${subtitle}</p>` : ''}
            ${showBundleNote ? `<p class="jp-cart-drawer__item-note">${this.escapeHtml(bundleNote)}</p>` : ''}
          </div>
          <span class="jp-cart-drawer__item-price">${this.formatMoney(item.final_line_price)}</span>
          <div class="jp-cart-drawer__item-controls">
            <div class="jp-cart-drawer__qty">
              <button type="button" class="jp-cart-drawer__qty-btn" data-jp-qty-decrease aria-label="Decrease quantity">&minus;</button>
              <span class="jp-cart-drawer__qty-value">${item.quantity}</span>
              <button type="button" class="jp-cart-drawer__qty-btn" data-jp-qty-increase aria-label="Increase quantity">&plus;</button>
            </div>
            <button type="button" class="jp-cart-drawer__item-remove" data-jp-item-remove aria-label="Remove item">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M3 5h12M7.5 5V3.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1V5M7.5 8.5v5M10.5 8.5v5M4.5 5l.6 9a1.5 1.5 0 0 0 1.5 1.4h4.8a1.5 1.5 0 0 0 1.5-1.4l.6-9" stroke="#472727" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  changeLineQuantity(button, delta) {
    const row = button.closest('[data-jp-line-key]');
    const key = row.getAttribute('data-jp-line-key');
    const valueEl = row.querySelector('.jp-cart-drawer__qty-value');
    const newQuantity = Math.max(0, parseInt(valueEl.textContent, 10) + delta);
    this.updateLine(key, newQuantity);
  }

  removeLine(button) {
    const row = button.closest('[data-jp-line-key]');
    const key = row.getAttribute('data-jp-line-key');
    this.updateLine(key, 0);
  }

  updateLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((res) => res.json())
      .then((cart) => {
        this.renderCart(cart);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jp-cart-drawer', cartData: cart });
        }
      })
      .catch(() => {});
  }

  loadRecommendations() {
    if (!this.cart || !this.recommendationsEl) return;

    if (this.cart.item_count === 0) {
      this.recommendationsEl.hidden = true;
      return;
    }

    // Request-sequence guard: loadRecommendations() fires on every cart
    // update, and the fetch below is async — if two cart changes happen in
    // quick succession (e.g. adding two different bundle products back to
    // back), their responses can resolve out of order and an older/stale
    // one would otherwise overwrite the newer, correct render (wrong
    // recommended products, wrong bundle-tier price). Only the response
    // matching the most recently started request is ever applied.
    const requestToken = (this.recommendationsRequestToken = (this.recommendationsRequestToken || 0) + 1);

    // Hide + clear immediately (not just on a failed/empty response): the
    // previous render's card can otherwise stay visible — and stale — for
    // the entire time this new request is in flight, since nothing else
    // clears it until (and unless) a response for THIS request arrives.
    this.recommendationsEl.hidden = true;
    this.recommendationsListEl.innerHTML = '';

    const productId = this.cart.items[this.cart.items.length - 1].product_id;
    const url = `${this.recommendationsUrl}.json?product_id=${productId}&limit=6`;

    // Bundle-tier config (same source the drawer's per-line upsell note and
    // the PDP widget use) — lets a recommended product show what it would
    // actually cost per unit if adding it pushes the cart into the next
    // bundle tier, instead of always showing its flat full price.
    const bundleConfig = (window.JPBundleUpsell && window.JPBundleUpsell.readConfig()) || null;
    const currentBundleQty =
      bundleConfig && window.JPBundleUpsell
        ? window.JPBundleUpsell.sumBundleQtyFromCartItems(bundleConfig, this.cart.items)
        : 0;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        if (requestToken !== this.recommendationsRequestToken) return; // a newer request superseded this one

        const recommendations = (data && data.products) || [];
        if (!recommendations.length) {
          this.recommendationsEl.hidden = true;
          return;
        }

        const cartProductIds = this.cart.items.map((item) => item.product_id);
        const products = recommendations.filter((product) => !cartProductIds.includes(product.id));
        if (!products.length) {
          this.recommendationsEl.hidden = true;
          return;
        }

        this.recommendationsListEl.innerHTML = products
          .map((product) => this.renderRecommendation(product, bundleConfig, currentBundleQty))
          .join('');
        this.recommendationsListEl.querySelectorAll('[data-jp-rec-add]').forEach((btn) => {
          btn.addEventListener('click', () => this.addRecommendation(btn));
        });
        this.recommendationsEl.hidden = false;
      })
      .catch(() => {
        if (requestToken !== this.recommendationsRequestToken) return;
        this.recommendationsEl.hidden = true;
      });
  }

  renderRecommendation(product, bundleConfig, currentBundleQty) {
    const image = product.featured_image
      ? `<img class="jp-cart-drawer__other-image" src="${product.featured_image.replace(/(\.[a-zA-Z0-9]+)(\?|$)/, '_160x$1$2')}" alt="${this.escapeHtml(product.title)}" loading="lazy">`
      : '';
    const variantId = product.variants && product.variants.length ? product.variants[0].id : '';

    // If adding this product would push the cart into (or further into) a
    // bundle tier, show that tier's real per-unit price instead of the
    // flat full price — e.g. "Rs. 427/-" rather than "Rs. 488/-" when one
    // bundle-eligible item is already in the cart.
    let priceCents = product.price;
    if (bundleConfig && bundleConfig.productIds.includes(product.id)) {
      const newQty = currentBundleQty + 1;
      const qualifyingTiers = bundleConfig.tiers
        .filter((tier) => newQty >= tier.qty && tier.totalPrice)
        .sort((a, b) => b.qty - a.qty);
      const tier = qualifyingTiers[0];
      if (tier) {
        const unitRupees = Math.floor(tier.totalPrice / tier.qty);
        priceCents = unitRupees * 100;
      }
    }

    // The "% OFF" sticker is derived from whichever price is actually being
    // shown (bundle-tier price when one applies, otherwise the flat price)
    // against the real compare-at, so it always matches the price next to it.
    let badge = '';
    if (product.compare_at_price_max > priceCents) {
      const pct = Math.round(((product.compare_at_price_max - priceCents) / product.compare_at_price_max) * 100);
      badge = `<div class="jp-cart-drawer__other-badge"><span>${pct}%</span><span>OFF</span></div>`;
    }

    return `
      <div class="jp-cart-drawer__other-card">
        ${badge}
        <div class="jp-cart-drawer__other-image-wrap">${image}</div>
        <div class="jp-cart-drawer__other-info">
          <p class="jp-cart-drawer__other-name">${this.escapeHtml(product.title)}</p>
          ${product.type ? `<p class="jp-cart-drawer__other-subtitle">${this.escapeHtml(product.type)}</p>` : ''}
          <p class="jp-cart-drawer__other-price">${this.formatMoney(priceCents)}</p>
        </div>
        <button type="button" class="jp-cart-drawer__other-add" data-jp-rec-add data-variant-id="${variantId}">
          Add to cart
        </button>
      </div>
    `;
  }

  addRecommendation(button) {
    const variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('cart-add-failed');
        return this.fetchAndRenderCart();
      })
      .then(() => {
        button.textContent = 'Added ✓';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1800);
      })
      .catch(() => {
        button.textContent = originalText;
        button.disabled = false;
      });
  }

  updateCartIconBubble(count) {
    const bubble = document.querySelector('#cart-icon-bubble');
    if (!bubble) return;
    let countEl = bubble.querySelector('.cart-count-bubble');
    if (count > 0) {
      if (!countEl) {
        countEl = document.createElement('div');
        countEl.className = 'cart-count-bubble';
        bubble.appendChild(countEl);
      }
      countEl.innerHTML = `<span aria-hidden="true">${count}</span>`;
    } else if (countEl) {
      countEl.remove();
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    return `Rs. ${(cents / 100).toFixed(0)}/-`;
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }
}

customElements.define('jp-cart-drawer', JpCartDrawer);

document.addEventListener('click', (event) => {
  const cartIcon = event.target.closest('#cart-icon-bubble');
  if (!cartIcon) return;

  const drawer = document.querySelector('jp-cart-drawer');
  if (!drawer) return;

  event.preventDefault();
  drawer.fetchAndRenderCart();
  drawer.open();
});
